import type { AgentAccessScope, PersonalWorkItem } from '@cdf/api-client-react';

export type WorkResumeReceiver = (item: PersonalWorkItem) => Promise<boolean>;
export function sameWorkScope(a: AgentAccessScope, b: AgentAccessScope) {
  return a.kind === b.kind && (a.tenantId ?? '') === (b.tenantId ?? '') && (a.entityId ?? '') === (b.entityId ?? '');
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Navigation carries identity only. It never carries private text or write authority. */
export function workResumeLink(item: PersonalWorkItem): string {
  if (item.source !== 'agent') {
    const url = new URL(item.resume.destination, 'https://work.invalid');
    const allowed = ['/content-hub/authority', '/content-hub/operations', '/content-hub/lead-magnet', '/content-hub/posts/', '/content-hub/mailing-list', '/research'];
    if (url.origin !== 'https://work.invalid' || !allowed.some(path => url.pathname === path || (path.endsWith('/') && url.pathname.startsWith(path)))) throw new Error('Open this item from its original workspace.');
    return url.pathname + url.search;
  }
  if (!uuid.test(item.resume.conversationId ?? '') || item.resume.conversationId !== item.id) throw new Error('The saved conversation could not be identified. Refresh your work.');
  const params = new URLSearchParams({ conversationId: item.id, mode: item.resume.mode ?? 'internal',
    workRevision: item.revision, workScope: item.scope.kind });
  if (item.scope.tenantId) params.set('workTenant', item.scope.tenantId);
  if (item.scope.entityId) params.set('workEntity', item.scope.entityId);
  return '/agent?' + params.toString();
}
export function requestedWork(search: string, fallback: AgentAccessScope | null) {
  const q = new URLSearchParams(search), id = q.get('conversationId');
  if (!id) return null;
  if (!uuid.test(id)) return { error: 'This conversation link is invalid.' } as const;
  const kind = q.get('workScope') ?? fallback?.kind;
  const scope: AgentAccessScope | null = kind === 'platform' ? { kind } : kind === 'tenant'
    ? { kind, tenantId: q.get('workTenant') ?? fallback?.tenantId }
    : kind === 'entity' ? { kind, tenantId: q.get('workTenant') ?? fallback?.tenantId, entityId: q.get('workEntity') ?? fallback?.entityId } : null;
  if (!scope || (scope.kind !== 'platform' && !uuid.test(scope.tenantId ?? '')) || (scope.kind === 'entity' && !uuid.test(scope.entityId ?? ''))) return { error: 'Select the original scope before opening this conversation.' } as const;
  const mode = q.get('mode') ?? 'internal';
  if (mode !== 'internal' && mode !== 'public_research') return { error: 'This conversation mode is invalid.' } as const;
  return { id, mode, scope, revision: q.get('workRevision') ?? undefined } as const;
}
